/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2021-07-22 00:00:00
 *
 *******************************************************/
//@flow

import _ from 'lodash';
import React, { Component } from 'react';
import { Button, Icon, TextStyle } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { CircleTickMajor, ExternalMinor, MobileCancelMajor } from '@shopify/polaris-icons';
import { connect } from 'react-redux';

import syncIcon from 'src/assets/images/common/sync-white.svg';

import { URL } from 'src/constants/links';
import SendRequestReport from 'src/components/containers/dashboard-v3/bannerInstallStatus/sendRequestReport';
import RequestSuccessModal from 'src/components/containers/dashboard-v3/bannerInstallStatus/requestSuccessModal';
import { getThemeReviewUrl } from 'src/helpers';

type Props = {
  loading: boolean
};

type State = {
  openPopup: boolean,
  activeSendRequestModal: boolean,
  activeSuccessModal: boolean
};

type ComposedProps = Props & WithI18nProps;

class InstallStatusPopup extends Component<ComposedProps, State> {
  state = {
    openPopup: true,
    activeSendRequestModal: false,
    activeSuccessModal: false
  };

  render() {
    const { openPopup, activeSendRequestModal, activeSuccessModal } = this.state;
    const { i18n, installFeatures, themeName, shopifyThemeName, isLiveTheme } = this.props;

    const featuresArr = Object.keys(installFeatures).filter(
      feature =>
        typeof installFeatures[feature] === 'string' && installFeatures[feature] === 'installed'
    );

    const featuresArrTextLabel = featuresArr
      .map(feature => {
        const featureLable = {
          filterCollection: i18n.translate(
            `AdminHTML.DashboardV3.ThemeSetupStatus.feature_filterCollection`
          ),
          filterSearch: i18n.translate(
            `AdminHTML.DashboardV3.ThemeSetupStatus.feature_filterSearch`
          ),
          instantSearch: i18n.translate(
            `AdminHTML.DashboardV3.ThemeSetupStatus.feature_instantSearch`
          ),
          recommendation: i18n.translate(
            `AdminHTML.DashboardV3.ThemeSetupStatus.feature_recommendation`
          )
        };

        return featureLable[feature];
      })
      .join(', ')
      .replace(/,(?=[^,]+$)/, ' and');

    const liveTheme = isLiveTheme ? ` ${i18n.translate('AdminHTML.DashboardV3.Common.live')}` : '';
    const themeInfo = `${themeName || shopifyThemeName}`;

    return (
      <div>
        {openPopup && (
          <div className="BC-InstallationStatus__Popup">
            <div className="icon-status">
              <Icon source={CircleTickMajor} color="success" />
            </div>
            <div className="info-status">
              <div>
                {i18n.translate('AdminHTML.DashboardV3.InstallStatusBanner.labelSuccess_popup')}
                <Button
                  onClick={() => this.toggleModal('openPopup')}
                  icon={MobileCancelMajor}
                  plain
                />
              </div>
              <div className="content content-success">
                <p>
                  {i18n.translate('AdminHTML.DashboardV3.InstallStatusBanner.contentSuccess01', {
                    t1: <TextStyle variation="strong">{featuresArrTextLabel}</TextStyle>,
                    t2: liveTheme,
                    t3: <TextStyle variation="strong">{themeInfo}</TextStyle>
                  })}
                </p>

                <p>
                  {i18n.translate(
                    'AdminHTML.DashboardV3.InstallStatusBanner.contentSuccess02_popup',
                    {
                      t1: (
                        <a href={URL['ThemeSetupDoc']} target="_blank" rel="noopener noreferrer">
                          {i18n.translate(
                            'AdminHTML.DashboardV3.InstallStatusBanner.contentSuccess03_t1'
                          )}
                          <Icon source={ExternalMinor} />
                        </a>
                      )
                    }
                  )}
                </p>
              </div>
              <div className="action-list">
                <Button primary onClick={() => this.handlePreviewClick()}>
                  {i18n.translate('AdminHTML.DashboardV3.InstallStatusBanner.buttonText01')}
                </Button>
                <Button onClick={() => this.toggleModal('activeSendRequestModal')}>
                  {i18n.translate('AdminHTML.DashboardV3.InstallStatusBanner.buttonText04')}
                </Button>
              </div>
            </div>
          </div>
        )}

        <Button
          id="BC-ExpReviewRatingModal-FloatingButton"
          primary
          onClick={() => this.toggleModal('openPopup')}>
          {openPopup ? (
            <div style={{ width: '16px', height: '2px', backgroundColor: '#fff' }} />
          ) : (
            <img src={syncIcon} alt="" style={{ width: '22px', height: '22px' }} />
          )}
        </Button>

        <SendRequestReport
          activeSendRequestModal={activeSendRequestModal}
          handleChangeModal={() => this.toggleModal('activeSendRequestModal')}
          handleAfterSend={() => this.toggleModal('activeSuccessModal')}
        />
        <RequestSuccessModal
          active={activeSuccessModal}
          handleChangeModal={() => this.toggleModal('activeSuccessModal')}
        />
      </div>
    );
  }

  toggleModal = fieldName => {
    const value = this.state[fieldName];

    if (
      !(
        fieldName === 'openPopup' &&
        (this.state.activeSendRequestModal || this.state.activeSuccessModal)
      )
    ) {
      this.setState({ [fieldName]: !value });
    }

    if (fieldName === 'activeSendRequestModal' && !value) {
      this.setState({ openPopup: false });
    }
  };

  handlePreviewClick = () => {
    const { installThemeId } = this.props;
    window.open(getThemeReviewUrl(installThemeId), '_blank');
  };
}

const mapStateToProps = (state, props) => {
  const syncStatus = _.get(state.install, 'syncStatus');
  const installThemeId = _.get(state.install, 'installThemeId');
  const themeName = _.get(state.install, 'installThemeName');
  const isLiveTheme = _.get(state.install, 'themeSetupInfoIsLiveTheme');
  const installFeatures = _.get(state.install, 'installFeatures');
  const themeInstallStatus = _.get(state.install, 'installStatus');
  const shopifyThemeName = _.get(state.install, 'installShopifyThemeName');

  return {
    host: _.get(state.oauth, 'host', ''),
    syncStatus,
    themeName,
    installThemeId,
    isLiveTheme,
    installFeatures,
    themeInstallStatus,
    shopifyThemeName
  };
};

export default withI18n()(connect(mapStateToProps)(InstallStatusPopup));
